import { useEffect, useState, useCallback } from 'react';
import { Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { fmtNumber } from '../lib/localeUtils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const POLL_MS = 120_000;

const STATUS_META = {
  healthy: { dot: 'bg-emerald-500', ring: 'ring-emerald-400/40', label: 'Duti AI ready' },
  unavailable: { dot: 'bg-rose-500', ring: 'ring-rose-400/40', label: 'Duti AI unavailable' },
  degraded: { dot: 'bg-amber-500', ring: 'ring-amber-400/40', label: 'Duti AI connection issue' },
  checking: { dot: 'bg-slate-400 animate-pulse', ring: 'ring-slate-300/40', label: 'Checking…' },
};

export const AIStatusBadge = () => {
  const [state, setState] = useState({ status: 'checking' });
  const [today, setToday] = useState(null);
  const navigate = useNavigate();

  const ping = useCallback(async () => {
    try {
      const { data } = await api.get('/ai/health', { timeout: 40_000 });
      setState(data);
    } catch (e) {
      setState({
        status: 'degraded',
        error: 'Could not reach the server to check Duti AI.',
        checked_at: new Date().toISOString(),
      });
    }
    // Separately fetch today's usage (cheap, aggregates in Mongo)
    try {
      const { data } = await api.get('/ai/usage', { params: { days: 1 }, timeout: 10_000 });
      setToday(data?.today || null);
    } catch (e) {
      console.warn('AI usage summary unavailable:', e);
    }
  }, []);

  useEffect(() => {
    ping();
    const id = setInterval(ping, POLL_MS);
    return () => clearInterval(id);
  }, [ping]);

  const meta = STATUS_META[state.status] || STATUS_META.checking;
  const checkedAt = state.checked_at
    ? new Date(state.checked_at).toLocaleTimeString()
    : '—';

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={(e) => {
              // Click = navigate to usage, Shift-click = force refresh
              if (e.shiftKey) { ping(); return; }
              navigate('/ai-usage');
            }}
            data-testid="ai-status-badge"
            className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/80 px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:border-border transition-colors"
            aria-label={meta.label}
            data-status={state.status}
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span className="hidden md:inline">AI</span>
            <span
              data-testid="ai-status-dot"
              className={`relative inline-flex h-2 w-2 rounded-full ring-2 ${meta.dot} ${meta.ring}`}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="text-xs max-w-[240px]">
          <div className="font-semibold">{meta.label}</div>
          {state.model && (
            <div className="text-muted-foreground mt-0.5">Model: {state.model}</div>
          )}
          {typeof state.latency_ms === 'number' && (
            <div className="text-muted-foreground">Latency: {state.latency_ms} ms</div>
          )}
          <div className="text-muted-foreground">Checked: {checkedAt}</div>
          {today && (
            <div className="mt-1.5 pt-1.5 border-t border-border/60">
              <div className="font-medium">Today</div>
              <div className="text-muted-foreground">
                {fmtNumber(today.calls || 0)} calls · {fmtNumber(today.tokens_total || 0)} tokens · ${Number(today.cost_usd || 0).toFixed(4)}
              </div>
            </div>
          )}
          {state.error && (
            <div className="text-rose-500 mt-1 break-words">{state.error}</div>
          )}
          <div className="text-muted-foreground mt-1 italic">Click to open usage · Shift-click to refresh</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default AIStatusBadge;
